import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Button, message as antMessage } from "antd";
import { ThunderboltOutlined, CloseOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import {
  DEFAULT_QUICK_QUESTION_THEME,
  type QuickQuestionTheme,
} from "./quickQuestionTheme";
import "./UCQuickQuestionFloating.css";

interface QuickQuestionFloatingProps {
  questions: Bot.QuickQuestion[];
  onQuestionClick?: (question: Bot.QuickQuestion) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  theme?: QuickQuestionTheme;
}

function withAlpha(color: string, alpha: number): string {
  const hex = color.replace("#", "");
  if (hex.length !== 6) {
    return color;
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function themeToCssVars(theme: QuickQuestionTheme): React.CSSProperties {
  return {
    "--qq-accent": theme.accent,
    "--qq-text": theme.text,
    "--qq-text-secondary": theme.textSecondary,
    "--qq-panel-bg": theme.panelBg,
    "--qq-panel-border": theme.panelBorder,
    "--qq-item-bg": theme.itemBg,
    "--qq-item-border": theme.itemBorder,
    "--qq-item-hover-bg": theme.itemHoverBg,
    "--qq-button-bg": theme.buttonBg,
    "--qq-accent-10": withAlpha(theme.accent, 0.1),
    "--qq-accent-12": withAlpha(theme.accent, 0.12),
    "--qq-accent-15": withAlpha(theme.accent, 0.15),
    "--qq-accent-25": withAlpha(theme.accent, 0.25),
    "--qq-accent-30": withAlpha(theme.accent, 0.3),
  } as React.CSSProperties;
}

const QuickQuestionFloating: React.FC<QuickQuestionFloatingProps> = ({
  questions,
  onQuestionClick,
  disabled = false,
  defaultExpanded = true,
  theme = DEFAULT_QUICK_QUESTION_THEME,
}) => {
  const intl = useIntl();
  const themeVars = useMemo(() => themeToCssVars(theme), [theme]);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const getEventCoordinates = (e: MouseEvent | TouchEvent) => {
    if ("touches" in e) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
  }, []);

  const handleMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      const { x: currentX, y: currentY } = getEventCoordinates(e);
      const deltaX = currentX - dragStart.x;
      const deltaY = currentY - dragStart.y;
      const panelSize = isExpanded ? 280 : 48;

      const newX = Math.max(
        0,
        Math.min(window.innerWidth - panelSize, dragPosition.x + deltaX)
      );
      const newY = Math.max(
        0,
        Math.min(window.innerHeight - panelSize, dragPosition.y + deltaY)
      );

      setDragPosition({ x: newX, y: newY });
      setDragStart({ x: currentX, y: currentY });
    },
    [isDragging, dragStart, dragPosition, isExpanded]
  );

  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);
      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd);
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleEnd);
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleEnd);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, handleMove, handleEnd]);

  useEffect(() => {
    const initializePosition = () => {
      if (dragPosition.x === 0 && dragPosition.y === 0) {
        const buttonSize = 48;
        const panelWidth = 280;
        const padding = 20;

        let initialX = window.innerWidth - buttonSize - padding - 60;
        let initialY = window.innerHeight - buttonSize - padding - 80;

        if (initialX + panelWidth > window.innerWidth - padding) {
          initialX = window.innerWidth - panelWidth - padding;
        }

        initialX = Math.max(
          padding,
          Math.min(initialX, window.innerWidth - buttonSize - padding)
        );
        initialY = Math.max(
          padding,
          Math.min(initialY, window.innerHeight - buttonSize - padding)
        );

        setDragPosition({ x: initialX, y: initialY });
      }
    };

    const timer = setTimeout(initializePosition, 100);
    return () => clearTimeout(timer);
  }, [dragPosition.x, dragPosition.y]);

  useEffect(() => {
    const handleResize = () => {
      const buttonSize = 48;
      const panelWidth = 280;
      const padding = 20;

      let newX = Math.max(
        padding,
        Math.min(window.innerWidth - buttonSize - padding - 60, dragPosition.x)
      );
      let newY = Math.max(
        padding,
        Math.min(window.innerHeight - buttonSize - padding - 80, dragPosition.y)
      );

      if (isExpanded && newX + panelWidth > window.innerWidth - padding) {
        newX = window.innerWidth - panelWidth - padding;
      }

      setDragPosition({ x: newX, y: newY });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dragPosition.x, dragPosition.y, isExpanded]);

  const handleQuestionClick = useCallback(
    async (question: Bot.QuickQuestion) => {
      if (isLoading || disabled) return;

      try {
        setIsLoading(true);
        if (onQuestionClick) {
          await onQuestionClick(question);
        }
        setIsExpanded(false);
      } catch (error) {
        console.error("快捷问题处理失败:", error);
        antMessage.error(intl.formatMessage({ id: "quick.question.error" }));
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, disabled, onQuestionClick, intl]
  );

  const toggleExpanded = useCallback(() => {
    if (disabled || isLoading) return;
    setIsExpanded(!isExpanded);
  }, [isExpanded, disabled, isLoading]);

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <div
      className="uchat-quick-qa-root"
      style={{
        ...themeVars,
        position: "fixed",
        top: `${dragPosition.y}px`,
        left: `${dragPosition.x}px`,
        zIndex: 200,
        transition: isDragging
          ? "none"
          : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {!isExpanded && (
        <Button
          type="default"
          shape="circle"
          icon={<ThunderboltOutlined />}
          onClick={toggleExpanded}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          disabled={disabled || isLoading}
          title={intl.formatMessage({ id: "quick.question.title" })}
          className={`uchat-quick-qa-toggle${
            isDragging ? " uchat-quick-qa-toggle-dragging" : ""
          }`}
          style={{
            cursor: isDragging
              ? "grabbing"
              : disabled
              ? "not-allowed"
              : "pointer",
          }}
        />
      )}

      {isExpanded && (
        <div
          className="uchat-quick-qa-panel"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="uchat-quick-qa-panel-header">
            <div className="uchat-quick-qa-panel-title">
              <ThunderboltOutlined className="uchat-quick-qa-panel-title-icon" />
              <span>{intl.formatMessage({ id: "quick.question.title" })}</span>
            </div>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={toggleExpanded}
              className="uchat-quick-qa-close"
            />
          </div>

          <div className="uchat-quick-qa-list">
            {questions.map((question) => (
              <Button
                key={question.id}
                type="text"
                size="small"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => handleQuestionClick(question)}
                className="uchat-quick-qa-item"
              >
                {question.question}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickQuestionFloating;
